// eslint-disable-next-line deprecate/import
import Link from 'next/link';

import type { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';
import { format, isPast, isValid } from 'date-fns';

import { Spinner } from '@uikit';
import { useModalController } from '@uikit';

import type { LaunchpadSalePool } from '@api';
import { LAUNCHPAD_SALE_POOL_STATUS } from '@api';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import KycRequiredModal from '@shared/common/components/modals/KycRequiredModal';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { LAUNCHPAD_INTERNAL_ROUTES } from '@shared/constants';
import { isDateWithinIntervalOfAnotherDate } from '@shared/helpers/date';
import { getIsSaleCompletedPoolStatus } from '@shared/helpers/getIsSaleCompletedPoolStatus';

import { useAuthModal } from '@launchpad/components/ui/buttons/ConnectWalletButton';
import { EVENT_START_DATE_COUNTDOWN_CUTOFF_INTERVAL_HOURS } from '@launchpad/constants';
import { UpcomingEventDate } from '@ui/uikit/components/common/UpcomingEventDate';

const isDateInPast = (startDate?: string) => {
  if (!startDate || !isValid(new Date(startDate))) {
    return false;
  }

  return isPast(new Date(startDate));
};

interface DepositButtonProps {
  projectSlug: string;
  pool: LaunchpadSalePool;
  userDeposit: number;
  className?: string;
  onJoinWhitelist?: () => void;
  title?: string;
  onSubscribe?: () => void;
  showSaveMySpotButton?: boolean;
}

const BaseDepositButton = ({
  disabled,
  className,
  ...props
}: ComponentPropsWithoutRef<'button'>) => {
  return (
    <button
      disabled={disabled}
      className={classNames(
        'ui-button ui-button-solid-primary',
        {
          'bg-neutral-200 text-neutral-50 cursor-not-allowed': disabled,
        },
        className,
      )}
      {...props}
    />
  );
};

const DepositButton = ({
  pool,
  projectSlug,
  userDeposit,
  className,
  onJoinWhitelist,
  onSubscribe,
  showSaveMySpotButton,
}: DepositButtonProps) => {
  const { login } = useAuthModal();
  const { isAuth, isLoading: isAuthLoading, user } = useAuth();
  const { track } = useMixPanel();
  const kycRequiredModal = useModalController();

  const trackJoinWhiteListClick = () => {
    track(MIXPANEL_EVENT_NAMES.LAUNCHPAD_WHITELIST_REQUIREMENTS_VIEWED, {
      distinct_id: user?.id,
      project: pool.attributes.name,
    });
  };

  const handleCompleteKYC = () => {
    if (!isAuth) {
      return login();
    }

    return kycRequiredModal.open();
  };

  if (getIsSaleCompletedPoolStatus(pool.attributes.status)) {
    return (
      <Link
        onClick={(event) => {
          if (!isAuth) {
            event.stopPropagation();
            event.preventDefault();

            return login();
          }
        }}
        href={isAuth ? LAUNCHPAD_INTERNAL_ROUTES.sale(projectSlug, pool.id) : ''}
        className={classNames(className, 'ui-button bg-neutral-200 text-neutral-50')}
      >
        Sale Completed
      </Link>
    );
  }

  // TODO: check why participate button shown first for 1s then it changes to deposit button

  if (isAuthLoading) {
    return (
      <BaseDepositButton disabled className={className}>
        <Spinner size="sm" color="disabled" />
      </BaseDepositButton>
    );
  }

  const isWhitelistRequired =
    pool.attributes?.meta?.whiteListExists && !pool.attributes?.meta?.isWhitelistParticipant;

  if (isWhitelistRequired) {
    const isWhitelistStarted = isDateInPast(pool?.whiteList?.attributes.startDate);
    const isWhitelistEnded = isDateInPast(pool?.whiteList?.attributes.endDate);

    if (!isWhitelistStarted) {
      const formattedStartDate =
        pool?.whiteList?.attributes.startDate &&
        isValid(new Date(pool.whiteList.attributes.startDate))
          ? format(new Date(pool.whiteList.attributes.startDate), 'dd.MM.yy, HH:mm')
          : 'TBA';

      return (
        <BaseDepositButton disabled className={className}>
          Whitelist starts {formattedStartDate}
        </BaseDepositButton>
      );
    } else if (isWhitelistEnded) {
      return (
        <BaseDepositButton disabled className={className}>
          Whitelist Closed
        </BaseDepositButton>
      );
    } else {
      return (
        <>
          {!user?.attributes?.kyc?.passed && (
            <>
              <BaseDepositButton
                onClick={handleCompleteKYC}
                className={classNames(className, 'mb-2')}
              >
                Complete KYC
              </BaseDepositButton>
              <KycRequiredModal {...kycRequiredModal.props} />
            </>
          )}
          <BaseDepositButton
            onClick={(event) => {
              if (!isAuth) {
                event.stopPropagation();
                event.preventDefault();

                return login();
              }

              trackJoinWhiteListClick();
              return onJoinWhitelist?.();
            }}
            disabled={!user?.attributes?.kyc?.passed}
            className={classNames(className, {
              '!mt-0': !user?.attributes?.kyc?.passed,
            })}
          >
            Join Whitelist
          </BaseDepositButton>
        </>
      );
    }
  }

  if (pool.attributes.status === LAUNCHPAD_SALE_POOL_STATUS.ANNOUNCED) {
    const poolStartDate = new Date(pool.attributes.startDate);

    const displayProjectStartDateAsCountdown = poolStartDate
      ? isDateWithinIntervalOfAnotherDate(
          poolStartDate,
          new Date(),
          EVENT_START_DATE_COUNTDOWN_CUTOFF_INTERVAL_HOURS,
        )
      : false;

    const label = pool.attributes.announcement || (
      <span>
        {'Sale starts '}
        {displayProjectStartDateAsCountdown ? 'in ' : ''}
        <UpcomingEventDate
          date={poolStartDate}
          mode={displayProjectStartDateAsCountdown ? 'countdown' : 'date'}
          countdownOptions={{ omitSecondsWhenIsDaysAway: true }}
        />
      </span>
    );

    return (
      <>
        {!user?.attributes?.kyc?.passed && (
          <>
            <BaseDepositButton
              onClick={handleCompleteKYC}
              className={classNames(className, 'mb-2')}
            >
              Complete KYC
            </BaseDepositButton>
            <KycRequiredModal {...kycRequiredModal.props} />
          </>
        )}
        <BaseDepositButton
          title={pool.attributes.announcement || undefined}
          disabled
          className={classNames(className, 'mb-2', {
            '!mt-0': !user?.attributes?.kyc?.passed,
          })}
        >
          <span className="truncate">{label}</span>
        </BaseDepositButton>
        {showSaveMySpotButton && (
          <BaseDepositButton
            onClick={() => {
              return onSubscribe?.();
            }}
            className={className}
          >
            Save My Spot
          </BaseDepositButton>
        )}
      </>
    );
  }

  return (
    <>
      {!user?.attributes?.kyc?.passed && (
        <>
          <BaseDepositButton onClick={handleCompleteKYC} className={classNames(className, 'mb-2')}>
            Complete KYC
          </BaseDepositButton>
          <KycRequiredModal {...kycRequiredModal.props} />
        </>
      )}
      <Link
        onClick={(event) => {
          if (!user?.attributes?.kyc?.passed) {
            event.stopPropagation();
            event.preventDefault();

            return false;
          }

          if (!isAuth) {
            event.stopPropagation();
            event.preventDefault();

            return login();
          }
        }}
        href={isAuth ? LAUNCHPAD_INTERNAL_ROUTES.sale(projectSlug, pool.id) : ''}
        className={classNames('ui-button', className, {
          'ui-button-solid-primary': user?.attributes?.kyc?.passed,
          'bg-neutral-200 text-neutral-50 cursor-not-allowed !mt-0': !user?.attributes?.kyc?.passed,
        })}
      >
        {userDeposit > 0 ? 'Deposit More' : 'Participate Now'}
      </Link>
    </>
  );
};

export default DepositButton;
